import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft, Mail, Phone, Building2, Globe, MapPin, Briefcase,
  Send, Inbox as InboxIcon, MessageSquare, Calendar, CheckCircle2, XCircle, Reply, Loader2, StickyNote,
  Tag, TrendingUp, DollarSign
} from "lucide-react";

const EVENT_ICONS = {
  campaign_sent:      { Icon: Send,        color: "#0044FF", label: "Campaign delivered" },
  campaign_failed:    { Icon: XCircle,     color: "#EF4444", label: "Campaign failed" },
  email_received:     { Icon: InboxIcon,   color: "#F59E0B", label: "Email received" },
  email_reply_sent:   { Icon: Reply,       color: "#10B981", label: "Reply sent" },
  tag_added:          { Icon: Tag,         color: "#8B5CF6", label: "Tag added" },
  tag_removed:        { Icon: Tag,         color: "#94A3B8", label: "Tag removed" },
  deal_created:       { Icon: DollarSign,  color: "#059669", label: "Deal created" },
  deal_stage_changed: { Icon: TrendingUp,  color: "#0EA5E9", label: "Deal stage updated" },
};

export default function ContactDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/contacts/${id}/timeline`);
        setData(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "var(--brand)" }} /></div>;
  if (!data) return <div className="p-10 text-center" style={{ color: "var(--text-soft)" }}>Contact not found. <Link to="/contacts" className="underline" style={{ color: "var(--brand)" }}>Back to contacts</Link></div>;

  const c = data.contact;
  const initials = (c.name || c.email).split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();
  const events = data.events || [];
  const location = [c.city, c.state, c.country].filter(Boolean).join(", ");

  return (
    <div className="space-y-6" data-testid="contact-detail-page">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm" className="gap-1"><Link to="/contacts"><ChevronLeft className="w-4 h-4" /> Back</Link></Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: profile */}
        <Card className="p-6 lg:col-span-1" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="flex items-start gap-3 mb-5">
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold" style={{ background: "color-mix(in srgb, var(--brand) 15%, transparent)", color: "var(--brand)" }}>{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="display text-2xl truncate">{c.name}</div>
              {c.job_title && <div className="text-sm" style={{ color: "var(--text-soft)" }}>{c.job_title}{c.company ? ` · ${c.company}` : ""}</div>}
              <div className="flex flex-wrap gap-1 mt-2">
                {(c.tags || []).map(t => (
                  <Badge key={t} variant="outline" className="text-xs" style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)", borderColor: "color-mix(in srgb, var(--brand) 25%, transparent)" }}>{t}</Badge>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Row Icon={Mail}     label="Email"          value={c.email} data-testid="contact-email" />
            {c.business_email && <Row Icon={Mail} label="Business email" value={c.business_email} />}
            {c.phone           && <Row Icon={Phone}        label="Phone"    value={c.phone} />}
            {c.business_name   && <Row Icon={Building2}    label="Business" value={c.business_name} />}
            {c.website         && <Row Icon={Globe}        label="Website"  value={c.website} link />}
            {location          && <Row Icon={MapPin}       label="Location" value={location} />}
            {(c.address_line1 || c.zip_code) && (
              <Row Icon={MapPin} label="Address" value={[c.address_line1, c.address_line2, c.zip_code].filter(Boolean).join(", ")} />
            )}
            {c.job_title       && <Row Icon={Briefcase}    label="Title"    value={c.job_title} />}
          </div>

          {c.notes && (
            <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2 mb-2 text-xs uppercase mono font-semibold" style={{ color: "var(--text-soft)" }}><StickyNote className="w-3.5 h-3.5" /> Notes</div>
              <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>{c.notes}</div>
            </div>
          )}

          <div className="mt-5 pt-5 border-t text-xs mono" style={{ borderColor: "var(--border)", color: "var(--text-soft)" }}>
            Added {new Date(c.created_at).toLocaleDateString()}
            {c.source && ` · via ${c.source}`}
          </div>
        </Card>

        {/* Right: timeline */}
        <Card className="p-6 lg:col-span-2" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="contact-timeline">
          <div className="flex items-center gap-2 mb-5">
            <Calendar className="w-4 h-4" style={{ color: "var(--brand)" }} />
            <h2 className="display text-xl">Activity timeline</h2>
            <Badge variant="outline" className="ml-auto">{events.length} event{events.length === 1 ? "" : "s"}</Badge>
          </div>

          {events.length === 0 ? (
            <div className="text-center py-16" style={{ color: "var(--text-soft)" }}>
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm">No activity yet</div>
              <div className="text-xs mt-1">Emails, campaign sends, and replies will appear here</div>
            </div>
          ) : (
            <ol className="relative border-l ml-3" style={{ borderColor: "var(--border)" }}>
              {events.map((e, i) => {
                const cfg = EVENT_ICONS[e.type] || EVENT_ICONS.campaign_sent;
                const Icon = cfg.Icon;
                return (
                  <li key={i} className="ml-6 mb-6" data-testid={`event-${i}`}>
                    <span className="absolute -left-3.5 flex items-center justify-center w-7 h-7 rounded-full" style={{ background: cfg.color + "20", border: `1px solid ${cfg.color}` }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                    </span>
                    <div className="text-xs mono" style={{ color: "var(--text-soft)" }}>
                      {e.at ? new Date(e.at).toLocaleString() : "—"} · {cfg.label}
                    </div>
                    <div className="mt-1 p-3 rounded-lg border" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
                      {(e.type === "campaign_sent" || e.type === "campaign_failed") && (
                        <>
                          <div className="text-sm font-semibold">{e.subject || e.campaign_name}</div>
                          <div className="text-xs mt-1 flex items-center gap-2" style={{ color: "var(--text-soft)" }}>
                            {e.status === "sent" ? <><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Delivered</> : <><XCircle className="w-3 h-3 text-red-500" /> Failed</>}
                            {e.provider && <span>· via {e.provider}</span>}
                            {e.campaign_id && <Link to={`/campaigns/${e.campaign_id}`} className="ml-auto underline" style={{ color: "var(--brand)" }}>View campaign</Link>}
                          </div>
                        </>
                      )}
                      {e.type === "email_received" && (
                        <>
                          <div className="text-sm font-semibold">{e.subject}</div>
                          <div className="text-xs mt-1" style={{ color: "var(--text-soft)" }}>
                            {e.tone && <Badge variant="outline" className="capitalize mr-1">{e.tone}</Badge>}
                            From <span className="font-medium">{e.from_name}</span>
                          </div>
                          {e.body_preview && <div className="text-sm mt-2 line-clamp-2" style={{ color: "var(--text-soft)" }}>{e.body_preview}</div>}
                          {e.inbox_id && <Link to={`/inbox?email=${e.inbox_id}`} className="text-xs underline mt-2 inline-block" style={{ color: "var(--brand)" }}>Open in Inbox →</Link>}
                        </>
                      )}
                      {e.type === "email_reply_sent" && (
                        <>
                          <div className="text-sm font-semibold">{e.subject}</div>
                          {e.body_preview && <div className="text-sm mt-1 line-clamp-2" style={{ color: "var(--text-soft)" }}>{e.body_preview}</div>}
                        </>
                      )}
                      {(e.type === "tag_added" || e.type === "tag_removed") && (
                        <div className="text-sm">
                          {e.type === "tag_added" ? "Tag added: " : "Tag removed: "}
                          <Badge variant="outline" className="capitalize ml-1" style={{ borderColor: "#8B5CF6", color: "#8B5CF6" }}>{e.tag}</Badge>
                        </div>
                      )}
                      {e.type === "deal_created" && (
                        <>
                          <div className="text-sm font-semibold">{e.title}</div>
                          <div className="text-xs mt-1 flex items-center gap-2" style={{ color: "var(--text-soft)" }}>
                            <span>{e.currency || "USD"} {Number(e.value || 0).toLocaleString()}</span>
                            <Badge variant="outline" className="capitalize">{e.stage}</Badge>
                            <Link to="/pipeline" className="ml-auto underline" style={{ color: "var(--brand)" }}>View pipeline</Link>
                          </div>
                        </>
                      )}
                      {e.type === "deal_stage_changed" && (
                        <div className="text-sm">
                          <span className="font-semibold">{e.title}</span> moved{" "}
                          <Badge variant="outline" className="capitalize mx-1">{e.from_stage}</Badge> →{" "}
                          <Badge variant="outline" className="capitalize mx-1" style={{ borderColor: "#0EA5E9", color: "#0EA5E9" }}>{e.to_stage}</Badge>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ Icon, label, value, link }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--text-soft)" }} />
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase mono" style={{ color: "var(--text-soft)" }}>{label}</div>
        {link ? (
          <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noreferrer" className="truncate block hover:underline" style={{ color: "var(--brand)" }}>{value}</a>
        ) : (
          <div className="truncate">{value}</div>
        )}
      </div>
    </div>
  );
}
